import { parseVideoUrl, backgroundEmbedUrl } from '@/utils/videoEmbed';

/**
 * Media di sfondo condiviso da ogni blocco a slide (Hero, HeroSlideshow,
 * PinnedReveal, HorizontalScroll, ScrollShowcase) — immagine o video
 * YouTube/Vimeo, sempre a riempire il contenitore posizionato (`relative`)
 * del chiamante, mai il proprio: `absolute inset-0`, stesso comportamento
 * per entrambi i tipi di media, mai il chiamante a doversi preoccupare di
 * quale dei due sta renderizzando.
 *
 * Il ritaglio "cover" di un video incorporato non può usare `object-fit`
 * (non si applica al contenuto di un iframe) — usa invece le container query
 * units (`cqw`/`cqh`, relative al contenitore diretto, non al viewport):
 * l'iframe è dimensionato più grande del necessario secondo il rapporto
 * 16:9 e centrato, l'overflow tagliato dal wrapper. Funziona identico sia
 * per uno sfondo a tutto schermo (Hero) sia per un pannello piccolo e
 * quadrato (ScrollShowcase) — un trick basato su vw/vh (comune altrove)
 * funzionerebbe solo nel primo caso.
 *
 * @param {{
 *   mediaType?: 'image' | 'video', image?: string, imageAlt?: string,
 *   videoUrl?: string, fallbackAlt?: string,
 *   loading?: 'lazy' | 'eager', fetchpriority?: 'high' | 'auto', className?: string,
 * }} props
 */
export function SlideMedia({
  mediaType = 'image', image, imageAlt = '', videoUrl, fallbackAlt = '',
  loading = 'lazy', fetchpriority = 'auto', className = '',
}) {
  if (mediaType === 'video') {
    const parsed = parseVideoUrl(videoUrl);
    if (!parsed) return null;
    return (
      <div className={'absolute inset-0 overflow-hidden [container-type:size] ' + className}>
        <iframe
          src={backgroundEmbedUrl(parsed)}
          title={imageAlt || fallbackAlt}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[177.78cqh] h-[100cqh] min-w-full min-h-[56.25cqw] pointer-events-none"
          allow="autoplay; encrypted-media"
          tabIndex={-1}
        />
      </div>
    );
  }

  if (!image) return null;
  return (
    <img
      src={image}
      alt={imageAlt || fallbackAlt}
      className={'absolute inset-0 w-full h-full object-cover ' + className}
      loading={loading}
      fetchpriority={fetchpriority}
      decoding="async"
    />
  );
}
